import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { initializePaddle, Paddle } from '@paddle/paddle-js';
import { User, PlanType } from '../types';
import { Link } from 'react-router-dom';

export const AccountPage: React.FC = () => {
    const { user, logout, openAuthModal } = useAuth();
    const [profile, setProfile] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isProfileLoading, setIsProfileLoading] = useState(true);
    const [uploadStatus, setUploadStatus] = useState('');
    const [paddle, setPaddle] = useState<Paddle | undefined>(undefined);

    useEffect(() => {
        if (user?.token) {
            fetchProfile();
        } else {
            setIsProfileLoading(false);
        }
    }, [user]);

    useEffect(() => {
        const initPaddle = async () => {
            try {
                const paddleInstance = await initializePaddle({
                    environment: 'production',
                    token: import.meta.env.VITE_PADDLE_CLIENT_SIDE_TOKEN,
                });
                setPaddle(paddleInstance);
            } catch (err) {
                console.error("Failed to initialize Paddle", err);
            }
        };
        initPaddle();
    }, []);

    useEffect(() => {
        // Automatically open auth modal for non-logged-in users
        if (user === null) {
            openAuthModal();
        }
    }, [user, openAuthModal]);

    const fetchProfile = async () => {
        setIsProfileLoading(true);
        try {
            const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}/user`, {
                headers: { Authorization: `Bearer ${user?.token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setProfile(data);
            }
        } catch (err) {
            console.error('Failed to fetch profile', err);
        } finally {
            setIsProfileLoading(false);
        }
    };

    const handleSrxUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !user?.token) return;

        // Check file size (1MB limit)
        const MAX_SIZE = 1 * 1024 * 1024; // 1MB
        if (file.size > MAX_SIZE) {
            setUploadStatus('File too large. Maximum size is 1MB.');
            return;
        }

        setUploadStatus('Getting upload URL...');
        try {
            // 1. Get presigned URL
            const urlRes = await fetch(`${import.meta.env.VITE_API_BASE_URL}/user/srx-upload-url`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${user.token}` }
            });
            if (!urlRes.ok) throw new Error('Failed to get upload URL');
            const { uploadUrl } = await urlRes.json();

            // 2. Upload to S3
            setUploadStatus('Uploading to S3...');
            const uploadRes = await fetch(uploadUrl, {
                method: 'PUT',
                body: file,
                headers: { 'Content-Type': 'application/xml' }
            });
            if (!uploadRes.ok) throw new Error('Upload failed');

            setUploadStatus('Upload successful!');
            setTimeout(() => setUploadStatus(''), 3000);
        } catch (err) {
            console.error(err);
            setUploadStatus('Upload failed.');
        }
    };

    if (!user) {
        return (
            <div className="page-container">
                <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                    <h2>Account</h2>
                    <p style={{ marginBottom: '1.5rem' }}>Please sign in to view your account.</p>
                    <button onClick={openAuthModal} className="button primary">
                        Sign In
                    </button>
                </div>
            </div>
        );
    }

    if (isProfileLoading) {
        return (
            <div className="page-container">
                <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                    <div className="spinner" style={{ margin: '0 auto 1rem' }} />
                    <p>Loading account...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2>My Account</h2>
                    <button className="button secondary small" onClick={logout}>Sign Out</button>
                </div>

                <div className="field">
                    <label>Username</label>
                    <div className="value">{user.email}</div>
                </div>


                <div className="divider" />

                {/* Expiring Subscription Warning (active but no next billing date = canceled pending expiry) */}
                {profile?.subscriptionStatus === 'active' && !profile?.nextBillingDate && profile?.currentPlan !== 'free' && (
                    <div style={{
                        background: '#fef3c7',
                        border: '1px solid #fbbf24',
                        color: '#78350f',
                        padding: '1rem',
                        borderRadius: 'var(--radius)',
                        marginBottom: '1rem'
                    }}>
                        <strong>⚠️ Subscription Expiring</strong>
                        <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem' }}>
                            Your subscription is set to expire at the end of your current billing period. Visit the pricing page to revert cancellation.
                        </p>
                    </div>
                )}

                <div className="row-group two-col">
                    <div className="field">
                        <label>Current Plan</label>
                        <div className="value" style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                            {profile?.currentPlan ? profile.currentPlan.charAt(0).toUpperCase() + profile.currentPlan.slice(1) : 'Free'}
                        </div>
                    </div>
                    <div className="field">
                        <label>Status</label>
                        <div className="value">
                            {profile?.subscriptionStatus ? (
                                <span className={`badge ${profile.subscriptionStatus === 'active' ? 'success' : 'warning'}`}>
                                    {profile.subscriptionStatus.toUpperCase()}
                                </span>
                            ) : (
                                <span className="badge">FREE</span>
                            )}
                        </div>
                    </div>
                </div>

                {profile?.nextBillingDate && (
                    <div className="field">
                        <label>Next Billing Date</label>
                        <div className="value">
                            {new Date(profile.nextBillingDate).toLocaleDateString()}
                        </div>
                    </div>
                )}

                <div className="divider" />

                <h3>Plan Limits</h3>
                <div className="limits-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
                    <div className="limit-item">
                        <label>Max Rows / File</label>
                        <div>{profile?.limits?.maxRowsPerFile ?? 50}</div>
                    </div>
                    <div className="limit-item">
                        <label>Max Target Langs</label>
                        <div>{profile?.limits?.maxTargetLangs ?? 2}</div>
                    </div>
                    <div className="limit-item">
                        <label>Custom SRX</label>
                        <div>{profile?.limits?.customSrx ? 'Enabled' : 'Disabled'}</div>
                    </div>
                </div>

                <div style={{ marginTop: '2rem' }}>
                    <Link to="/pricing" className="button primary">
                        Manage Subscription
                    </Link>
                </div>
            </div>

            <div className="card">
                <h2>Global Settings</h2>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                    Upload a global SRX segmentation file to be used as a default for all your conversions.
                </p>

                <div className="field">
                    <label>Global SRX Rules</label>
                    <div className="file-upload-area">
                        <input type="file" accept=".srx,.xml" onChange={handleSrxUpload} disabled={isLoading || !profile?.limits?.customSrx} />
                        <p>
                            {uploadStatus || (profile?.limits?.customSrx ? 'Drag & drop or click to upload .srx file' : 'Upgrade to enable Custom SRX')}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};
